import {
  Box,
  Columns,
  Container,
  Heading,
  PresentationLink,
  Stack,
  Text,
  Visible,
} from '@spaceship-fspl/components';
import {
  fontSize,
  fontWeight,
  lineHeight,
  match,
} from '@spaceship-fspl/styles';
import { TrackingCategory } from '@spaceship-fspl/tracking';
import { Card, PanelSection } from 'components';
import { MobileOptions } from 'components/mobile-options';
import BackgroundImage from 'images/first-home_homebuyer-programs_background.svg';
import fhbgIconImage from 'images/first-home_homebuyer-programs_fhbg.svg';
import fhssIconImage from 'images/first-home_homebuyer-programs_fhss.svg';
import Link from 'next/link';
import React, { useState } from 'react';
import styled from 'styled-components';

interface Program {
  type?: string | null;
  title?: string | null;
  description?: React.ReactNode;
  learnMoreText?: string | null;
  learnMoreLink?: string | null;
}

interface HomebuyerProgramsPanelProps {
  title?: string | null;
  tagline?: React.ReactNode;
  programs?: Array<Program>;
}

export const HomebuyerProgramsPanel: React.FC<HomebuyerProgramsPanelProps> = ({
  title,
  tagline,
  programs,
}) => {
  const [selectedProgram, onSelectProgram] = useState<string>('FHSS');

  return (
    <TrackingCategory category="Homebuyer Programs Panel">
      <PanelSection backgroundColor="indigo.100">
        <Stack spaceY={{ xs: 'lg', lg: 'xl' }}>
          <Container>
            <Columns alignX="center">
              <Columns.Column width={{ xs: 1, md: 6 / 8, lg: 8 / 12 }}>
                <Stack spaceY={{ xs: 'sm', lg: 'md' }}>
                  <Heading
                    component="h2"
                    variant={2}
                    align="center"
                    color="neutral.000"
                  >
                    {title}
                  </Heading>

                  <Text
                    variant={1}
                    align="center"
                    color="neutral.070"
                    component="div"
                  >
                    {tagline}
                  </Text>
                </Stack>
              </Columns.Column>
            </Columns>
          </Container>

          <Visible
            isHidden={{ xs: true, md: false }}
            displayValue="block"
            component="div"
          >
            <StyledBackgroundBox>
              <Container>
                <Columns
                  alignX="center"
                  spaceX={{
                    xs: 'none',
                    lg: 'xl',
                  }}
                  spaceY={{ xs: 'md', lg: 'none' }}
                >
                  {programs?.map((program) => {
                    let iconImage = '';

                    switch (program.type) {
                      case 'FHSS':
                        iconImage = fhssIconImage;
                        break;
                      case 'FHBG':
                        iconImage = fhbgIconImage;
                        break;
                    }

                    return (
                      <Columns.Column
                        width={{ xs: 1, lg: 1 / 2, xl: 5 / 12 }}
                        key={program.type}
                      >
                        <ProgramCard {...program} icon={iconImage} />
                      </Columns.Column>
                    );
                  })}
                </Columns>
              </Container>
            </StyledBackgroundBox>
          </Visible>

          {!!programs && programs.length > 0 && (
            <Visible
              isHidden={{ md: true }}
              displayValue="block"
              component="div"
            >
              <MobileOptions
                variant="dark"
                options={
                  programs.map((program) => ({
                    ...program,
                    label: program.type ?? '',
                    value: program.type ?? '',
                  })) ?? []
                }
                selectedOption={selectedProgram}
                onChange={(value) => onSelectProgram(value)}
                renderOption={(props) => {
                  let iconImage = '';

                  switch (props.type) {
                    case 'FHSS':
                      iconImage = fhssIconImage;
                      break;
                    case 'FHBG':
                      iconImage = fhbgIconImage;
                      break;
                  }

                  return <ProgramCard {...props} icon={iconImage} />;
                }}
              />
            </Visible>
          )}
        </Stack>
      </PanelSection>
    </TrackingCategory>
  );
};

const ProgramCard: React.FC<
  Program & {
    icon: string;
  }
> = ({ icon, title, description, learnMoreText, learnMoreLink }) => {
  return (
    <Card
      variant="light"
      padding={{ xs: 'md', md: 'lg', lg: 'xl' }}
      fullHeight={true}
    >
      <Box display="flex" flexDirection="column" height="100%">
        <Box flex={1}>
          <Stack spaceY={{ xs: 'xs', md: 'sm', lg: 'md' }}>
            <Stack spaceY="xxs">
              <StyledProgramIconImg src={icon} alt={title ?? ''} />

              <StyledProgramTitle variant={4}>{title}</StyledProgramTitle>
            </Stack>

            <StyledProgramDescription
              variant={2}
              color="neutral.085"
              component="div"
            >
              {description}
            </StyledProgramDescription>
          </Stack>
        </Box>

        {learnMoreText && learnMoreLink && (
          <Box marginTop={{ xs: 'sm', lg: 'md' }}>
            <Link href={learnMoreLink}>
              <PresentationLink
                size="md"
                icon="arrow"
                color="indigo.070"
                component="span"
              >
                {learnMoreText}
              </PresentationLink>
            </Link>
          </Box>
        )}
      </Box>
    </Card>
  );
};

const StyledBackgroundBox = styled.div`
  ${match('md')`
    background-image: url(${BackgroundImage});
    background-position: bottom;
    background-repeat: no-repeat;
    background-size: 100%;
  `}

  ${match('lg')`
    background-size: 90%;
  `};

  ${match('xl')`
    background-size: 1200px;
  `};
`;

const StyledProgramTitle = styled(Heading)`
  ${fontSize({ md: '28px', lg: '32px' })}
  ${lineHeight({ md: '40px', lg: '46px' })}
`;

const StyledProgramDescription = styled(Text)`
  ${fontSize({ md: '20px', lg: '24px' })}
  ${fontWeight({ md: 500 })}
  ${lineHeight({ md: '32px', lg: '38px' })}
`;

const StyledProgramIconImg = styled.img`
  .carousel .slide & {
    width: 100px;
  }
`;
